/**
 * MorphingHemisphere - A hemisphere geometry that morphs between curved and flat.
 *
 * This component creates a hemisphere mesh that can smoothly transition
 * between a full hemisphere (curvature = 1) and a flat plane (curvature = 0).
 * The camera is inside the hemisphere, looking at the inner surface.
 *
 * Performance: Uses GPU shader-based morphing - geometry is created once,
 * and curvature interpolation happens entirely on the GPU.
 */

import { useMemo, useRef, useEffect } from 'react'
import * as THREE from 'three'
import { GLOBE_CONFIG } from '@/domain/globe/constants'
import {
  basicMorphVertexShader,
  solidColorFragmentShader,
} from '@/domain/globe/shaders'
import { createHemisphereGeometry } from '@/domain/globe/utils'

interface MorphingHemisphereProps {
  /** Radius of the hemisphere in world units */
  radius?: number
  /** Number of segments for geometry detail */
  segments?: number
  /** Curvature factor: 0 = flat plane, 1 = full hemisphere */
  curvature?: number
  /** Color of the hemisphere (used when no texture) */
  color?: THREE.ColorRepresentation
  /** Whether to render wireframe */
  wireframe?: boolean
}

/**
 * MorphingHemisphere component for the globe visualization.
 *
 * Renders a hemisphere that can morph between curved and flat states.
 * Camera should be positioned at the center (0, 0, 0) looking outward.
 *
 * Performance: Geometry is created ONCE at mount. Curvature changes only
 * update a shader uniform, which is extremely fast (no geometry recreation).
 *
 * @example
 * ```tsx
 * <Canvas>
 *   <MorphingHemisphere curvature={0.5} />
 * </Canvas>
 * ```
 */
export function MorphingHemisphere({
  radius = GLOBE_CONFIG.SPHERE_RADIUS,
  segments = GLOBE_CONFIG.WIDTH_SEGMENTS,
  curvature = 1.0,
  color = 0x1a1a2e,
  wireframe = false,
}: MorphingHemisphereProps) {
  const materialRef = useRef<THREE.ShaderMaterial>(null)

  // Create geometry ONCE with both position sets (no curvature dependency!)
  const geometry = useMemo(
    () => createHemisphereGeometry(radius, segments, segments / 2),
    [radius, segments]
  )

  // Convert color to THREE.Color
  const threeColor = useMemo(() => new THREE.Color(color), [color])

  // Create uniforms object - memoized to keep reference stable
  const uniforms = useMemo(
    () => ({
      curvature: { value: curvature },
      color: { value: threeColor },
    }),
    // Only recreate when color changes, curvature updates via ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threeColor]
  )

  // Update curvature uniform when it changes (no geometry recreation!)
  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.uniforms.curvature.value = curvature
    }
  }, [curvature])

  return (
    <mesh name="morphing-hemisphere" geometry={geometry} renderOrder={0}>
      <shaderMaterial
        ref={materialRef}
        vertexShader={basicMorphVertexShader}
        fragmentShader={solidColorFragmentShader}
        uniforms={uniforms}
        side={THREE.DoubleSide}
        wireframe={wireframe}
        depthWrite={false}
      />
    </mesh>
  )
}
